import { clamp, formatLabel, formatRate, formatTagTime, formatTime, thumbOffset, volumeIcon } from "./format";

function setIcon(container, name) {
    container.querySelectorAll("[data-icon]").forEach(icon => {
        icon.hidden = icon.dataset.icon !== name;
    });
}

// render() only reads state: it updates every element from the full state, every call.
export function createRender(player) {
    const { root, el, cfg, state } = player;

    return function render() {
        const displayTime = state.scrubTime !== null ? state.scrubTime : state.currentTime;
        const percentOf = t => (state.duration > 0 ? clamp((t / state.duration) * 100, 0, 100) : 0);
        const percent = percentOf(displayTime);
        const disabled = player.isSeekDisabled();
        const isScrubbing = state.isDragging || state.isFocused || state.isHovered;
        const previewTime = state.isDragging ? (state.scrubTime !== null ? state.scrubTime : state.currentTime) : state.hoverTime;
        const previewVisible = previewTime !== null && !disabled && (state.isHovered || state.isDragging);

        root.setAttribute("aria-busy", String(state.isBuffering));
        el.currentTime.textContent = formatTime(state.currentTime);
        el.duration.textContent = formatTime(state.duration);

        el.fill.style.inlineSize = `${percent}%`;
        el.thumbAnchor.style.insetInlineStart = thumbOffset(percent);
        el.thumb.classList.toggle("playback-progress__thumb--visible", isScrubbing);
        el.previewAnchor.style.insetInlineStart = thumbOffset(previewTime !== null ? percentOf(previewTime) : 0);
        el.previewLabel.classList.toggle("playback-progress__time-label--visible", previewVisible);
        el.previewLabel.classList.toggle("playback-progress__time-label--over-handle", state.isDragging);
        el.previewTime.textContent = formatTagTime(previewTime !== null ? previewTime : displayTime);

        el.seek.max = String(state.duration || 0);
        el.seek.value = String(displayTime);
        el.seek.disabled = disabled;
        el.seek.setAttribute(
            "aria-valuetext",
            formatLabel(cfg.labels.seekValue, {
                current: formatTime(displayTime),
                total: formatTime(state.duration)
            })
        );

        const rate = formatRate(state.playbackRate);
        (el.speedTrigger.querySelector("[data-speed-label]") || el.speedTrigger).textContent = rate;
        el.speedTrigger.setAttribute("aria-label", formatLabel(cfg.labels.speed, { rate }));
        el.speedTrigger.disabled = state.hasError;
        el.speedTrigger.setAttribute("aria-expanded", String(state.menuOpen));
        el.speedList.hidden = !state.menuOpen;
        el.speedOptions.forEach((opt, i) => {
            opt.setAttribute("aria-selected", String(Number(opt.dataset.rate) === state.playbackRate));
            opt.classList.toggle("playback-speed__option--active", i === state.activeOption);
        });

        el.skips.forEach(b => {
            b.disabled = disabled;
        });

        el.play.disabled = state.hasError;
        el.play.setAttribute("aria-label", state.isPlaying ? cfg.labels.pause : cfg.labels.play);
        setIcon(el.play, state.isPlaying ? "pause" : "play_arrow");
        el.play.classList.toggle("playback-audio__play--buffering", state.isBuffering);
        el.spinner.hidden = !state.isBuffering;

        const volPercent = state.isMuted ? 0 : Math.round(state.volume * 100);
        el.mute.setAttribute("aria-label", state.isMuted ? cfg.labels.unmute : cfg.labels.mute);
        setIcon(el.mute, volumeIcon(state.volume, state.isMuted));
        el.volume.value = String(volPercent);
        el.volume.setAttribute("aria-valuetext", `${volPercent}%`);
        el.volumeFill.style.inlineSize = `${volPercent}%`;
        el.volumeThumb.style.insetInlineStart = thumbOffset(volPercent);

        el.error.hidden = !state.hasError;
        el.retry.hidden = !state.hasError;
        if (el.caption) el.caption.hidden = state.hasError;
        el.info.classList.toggle("playback-audio__info--empty", !(state.hasError || el.caption));
    };
}
